import React, { useMemo, useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Platform,
  ActivityIndicator,
  KeyboardAvoidingView,
  Alert,
  Image,
  Modal,
} from 'react-native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RouteProp } from '@react-navigation/native';
import { supabase } from '../lib/supabase';
import type { UserProductWithDetails } from '../types/newDatabaseTypes';
import type { CacheStackParamList } from '../types/navigation';
import { getUserProductById, updateUserProduct, deleteUserProduct } from '../utils/userProductStorage';
import { ItemValuation } from '../components/common/ItemValuation';
import { useTheme } from '../styles/ThemeProvider';
import { useCommonStyles } from '../styles/useCommonStyles';
import { ConditionBadge } from '../components/common/ConditionBadge';
import { VariantPicker } from '../components/common/VariantPicker';
import { DetailGroup } from '../components/common/DetailGroup';
import { DetailRow } from '../components/common/DetailRow';
import { listSiblingVariants, type SiblingVariant } from '../lib/variantOptions';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { getAllBrands, getFamiliesByBrand, getModelsByFamily, findOrCreateProductHierarchy, createBrand, createProductFamily, createProductModel, Brand, ProductModel, ProductFamily, extractFamilyFromModelName } from '../utils/brandModelStorage';
import { isUserVerified, getCurrentUserId } from '../utils/userPermissions';
import { CreateItemModal } from '../components/CreateItemModal';

type Props = {
  navigation: NativeStackNavigationProp<CacheStackParamList, 'EditExistingItem'>;
  route: RouteProp<CacheStackParamList, 'EditExistingItem'>;
};

type ItemCondition = 'New' | 'Like New' | 'Excellent' | 'Good' | 'Fair' | 'Poor';

const CONDITIONS: ItemCondition[] = ['New', 'Like New', 'Excellent', 'Good', 'Fair', 'Poor'];

export default function EditExistingItemScreen({ navigation, route }: Props) {
  const theme = useTheme();
  const commonStyles = useCommonStyles();
  // Confidence buckets: high → accent, mid → accentDisabled, low → destructive
  const confidenceTone = (score: number) => {
    if (score >= 0.9) return theme.colors.accent;
    if (score >= 0.6) return theme.colors.accentDisabled;
    return theme.colors.destructive;
  };
  const styles = useMemo(
    () =>
      StyleSheet.create({
        centered: {
          justifyContent: 'center',
          alignItems: 'center',
        },
        scrollView: {
          flex: 1,
        },
        imageContainer: {
          width: '100%',
          height: 280,
          backgroundColor: theme.colors.backgroundSurface,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
        },
        image: {
          width: '100%',
          height: '100%',
          resizeMode: 'cover',
        },
        formContainer: {
          padding: theme.spacing.lg,
        },
        fieldGroup: {
          marginBottom: theme.spacing.lg,
        },
        textArea: {
          minHeight: 100,
          textAlignVertical: 'top',
        },
        confidenceBadge: {
          alignSelf: 'flex-start',
          paddingVertical: theme.spacing.sm,
          paddingHorizontal: theme.spacing.md,
          borderWidth: theme.borders.borderWidth,
          borderRadius: theme.radius.radiusLg,
          marginTop: theme.spacing.sm,
          backgroundColor: 'transparent',
        },
        confidenceText: {
          ...theme.typography.md,
          fontFamily: theme.fontFamily.bodyMedium,
        },
        editableHint: {
          ...theme.typography.xs,
          color: theme.colors.accent,
          fontStyle: 'italic',
          letterSpacing: 0,
        },
        editButton: {
          flexDirection: 'row',
          alignItems: 'center',
          paddingVertical: theme.spacing.sm,
          paddingHorizontal: theme.spacing.md,
          backgroundColor: 'transparent',
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.colors.accent,
          borderRadius: theme.radius.radiusMd,
          gap: theme.spacing.xs,
        },
        identityEditButton: {
          alignSelf: 'flex-end',
          marginTop: theme.spacing.sm,
        },
        editButtonText: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.accent,
        },
        pickerButton: {
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          paddingVertical: theme.spacing.md,
          paddingHorizontal: theme.spacing.lg,
          marginTop: theme.spacing.sm,
        },
        placeholderText: {
          ...theme.typography.md,
          color: theme.colors.textTertiary,
        },
        selectedText: {
          ...theme.typography.md,
          color: theme.colors.textPrimary,
        },
        cancelEditButton: {
          alignSelf: 'flex-start',
          paddingVertical: theme.spacing.sm,
          paddingHorizontal: theme.spacing.md,
          backgroundColor: 'transparent',
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.colors.destructive,
          borderRadius: theme.radius.radiusMd,
          marginTop: theme.spacing.sm,
          marginBottom: theme.spacing.lg,
        },
        cancelEditText: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.destructive,
        },
        footer: {
          flexDirection: 'row',
          gap: theme.spacing.sm,
          padding: theme.spacing.lg,
          borderTopWidth: theme.borders.borderWidth,
          borderTopColor: theme.borders.borderColor,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        footerButton: {
          flex: 1,
        },
        dialogButtonRow: {
          flexDirection: 'row',
          gap: theme.spacing.sm,
        },
        dialogButton: {
          flex: 1,
        },
        bottomSheetOverlay: {
          flex: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          justifyContent: 'flex-end',
        },
        bottomSheet: {
          backgroundColor: theme.colors.backgroundElevated,
          borderTopLeftRadius: theme.radius.radiusLg,
          borderTopRightRadius: theme.radius.radiusLg,
          maxHeight: '70%',
          borderTopWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
        },
        pickerHeader: {
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: theme.spacing.lg,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
        },
        pickerTitle: {
          ...theme.typography.h4,
          color: theme.colors.textPrimary,
        },
        pickerList: {
          maxHeight: 400,
        },
        pickerItem: {
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: theme.spacing.lg,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
        },
        pickerItemSelected: {
          backgroundColor: theme.colors.backgroundSurface,
        },
        pickerItemText: {
          ...theme.typography.md,
          color: theme.colors.textPrimary,
        },
        pickerItemTextSelected: {
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.accent,
        },
        emptyState: {
          padding: theme.spacing.xxl,
          alignItems: 'center',
        },
        emptyStateText: {
          ...theme.typography.md,
          color: theme.colors.textTertiary,
          textAlign: 'center',
        },
        addNewButton: {
          flexDirection: 'row',
          alignItems: 'center',
          padding: theme.spacing.lg,
          backgroundColor: theme.colors.backgroundSurface,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.colors.accent,
          gap: theme.spacing.sm,
        },
        addNewButtonText: {
          ...theme.typography.md,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.accent,
        },
        verificationMessage: {
          flexDirection: 'row',
          alignItems: 'center',
          padding: theme.spacing.lg,
          backgroundColor: theme.colors.backgroundSurface,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
          gap: theme.spacing.sm,
        },
        verificationMessageText: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
          flex: 1,
        },
      }),
    [theme],
  );
  const { itemId } = route.params;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [item, setItem] = useState<UserProductWithDetails | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [condition, setCondition] = useState<ItemCondition>('Good');
  const [estimatedValueMin, setEstimatedValueMin] = useState('');
  const [estimatedValueMax, setEstimatedValueMax] = useState('');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [brands, setBrands] = useState<Brand[]>([]);
  const [families, setFamilies] = useState<ProductFamily[]>([]);
  const [models, setModels] = useState<ProductModel[]>([]);
  const [selectedBrandId, setSelectedBrandId] = useState<string>('');
  const [selectedFamilyId, setSelectedFamilyId] = useState<string>('');
  const [selectedModelId, setSelectedModelId] = useState<string>('');
  const [isEditingBrandModel, setIsEditingBrandModel] = useState(false);
  const [confidenceScore, setConfidenceScore] = useState<number | null>(null);
  const [showBrandPicker, setShowBrandPicker] = useState(false);
  const [showFamilyPicker, setShowFamilyPicker] = useState(false);
  const [showModelPicker, setShowModelPicker] = useState(false);
  const [showCreateBrand, setShowCreateBrand] = useState(false);
  const [showCreateFamily, setShowCreateFamily] = useState(false);
  const [showCreateModel, setShowCreateModel] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  // Variant picker (T4b). The chosen sibling is applied in handleSave; the
  // existing brand/family/model edit flow is untouched.
  const [siblingVariants, setSiblingVariants] = useState<SiblingVariant[]>([]);
  const [showVariantPicker, setShowVariantPicker] = useState(false);
  const [pickedVariant, setPickedVariant] = useState<SiblingVariant | null>(null);

  useEffect(() => {
    fetchItem();
    loadBrands();
    checkUserVerification();
  }, [itemId]);

  // Load catalogued sibling variants for this item's model so the user can
  // correct the variant (T4b). Hidden unless ≥2 exist (Q2).
  useEffect(() => {
    const modelId = item?.variant?.model?.id ?? item?.variant?.model_id;
    if (!modelId) {
      setSiblingVariants([]);
      return;
    }
    let cancelled = false;
    listSiblingVariants(modelId.toString(), item?.variant_id ?? null).then((variants) => {
      if (!cancelled) setSiblingVariants(variants);
    });
    return () => {
      cancelled = true;
    };
  }, [item?.variant?.model?.id, item?.variant?.model_id, item?.variant_id]);

  const checkUserVerification = async () => {
    const verified = await isUserVerified();
    const currentUserId = await getCurrentUserId();
    setIsVerified(verified);
    setUserId(currentUserId);
  };

  useEffect(() => {
    if (item?.images) {
      getImageUrl();
    }
  }, [item]);

  const getImageUrl = async () => {
    if (!item?.images) return;

    try {
      // Handle the new images structure (array of objects with storage_path)
      const images = item.images as any;
      if (images && Array.isArray(images) && images.length > 0) {
        const imagePath = images[0].storage_path || images[0];
        const { data: { publicUrl } } = supabase
          .storage
          .from('item-images')
          .getPublicUrl(imagePath);

        setImageUrl(publicUrl);
      }
    } catch (error) {
      console.error('Error getting image URL:', error);
    }
  };

  const fetchItem = async () => {
    try {
      const product = await getUserProductById(itemId);

      if (!product) {
        Alert.alert('Error', 'Item not found');
        navigation.goBack();
        return;
      }

      setItem(product);

      // Map the new data structure to form fields
      const brandName = product.variant?.model?.family?.brand?.name || '';
      const modelName = product.variant?.model?.name || '';
      const imageAnalysis = product.image_analysis as any;
      const categoryName = imageAnalysis?.category || '';
      const descriptionText = imageAnalysis?.description || '';

      setName(modelName || brandName || 'Product');
      setDescription(descriptionText);
      setCategory(categoryName);
      setCondition(product.condition as ItemCondition || 'Good');
      setEstimatedValueMin(''); // Not available in new schema
      setEstimatedValueMax(''); // Not available in new schema
      setBrand(brandName);
      setModel(modelName);
      setPurchasePrice(product.purchase_price?.toString() || '');
      setPurchaseDate(product.purchase_period || '');

      // Extract confidence score from image_analysis
      const imageAnalysisData = product.image_analysis as any;
      const confidence = imageAnalysisData?.confidence_score || imageAnalysisData?.confidenceScore || null;
      setConfidenceScore(confidence);

      // Set selected brand, family, and model IDs for dropdowns
      const brandId = product.variant?.model?.family?.brand?.id;
      const familyId = product.variant?.model?.family?.id;
      const modelId = product.variant?.model?.id;

      if (brandId) {
        setSelectedBrandId(brandId.toString());
        // Load families for this brand
        loadFamiliesForBrand(brandId.toString());
      }
      if (familyId) {
        setSelectedFamilyId(familyId.toString());
        // Load models for this family
        loadModelsForFamily(familyId.toString());
      }
      if (modelId) {
        setSelectedModelId(modelId.toString());
      }
    } catch (err) {
      console.error('Error fetching item:', err);
      Alert.alert('Error', 'Failed to load item');
      navigation.goBack();
    } finally {
      setLoading(false);
    }
  };

  const loadBrands = async () => {
    try {
      const allBrands = await getAllBrands();
      setBrands(allBrands);
    } catch (error) {
      console.error('Error loading brands:', error);
    }
  };

  const loadFamiliesForBrand = async (brandId: string) => {
    try {
      const brandFamilies = await getFamiliesByBrand(brandId);
      setFamilies(brandFamilies);
    } catch (error) {
      console.error('Error loading families:', error);
    }
  };

  const loadModelsForFamily = async (familyId: string) => {
    try {
      const familyModels = await getModelsByFamily(familyId);
      setModels(familyModels);
    } catch (error) {
      console.error('Error loading models:', error);
    }
  };

  const handleBrandChange = async (brandId: string) => {
    setSelectedBrandId(brandId);
    setSelectedFamilyId(''); // Reset family selection
    setSelectedModelId(''); // Reset model selection
    setFamilies([]); // Clear families
    setModels([]); // Clear models
    await loadFamiliesForBrand(brandId);
    // Auto-open family picker after brand selection
    setTimeout(() => setShowFamilyPicker(true), 300);
  };

  const handleFamilyChange = async (familyId: string) => {
    setSelectedFamilyId(familyId);
    setSelectedModelId(''); // Reset model selection
    setModels([]); // Clear models
    if (familyId) {
      await loadModelsForFamily(familyId);
      // Auto-open model picker after family selection
      setTimeout(() => setShowModelPicker(true), 300);
    }
  };

  const handleModelChange = (modelId: string) => {
    setSelectedModelId(modelId);
  };

  // Creation handlers
  const handleCreateBrand = async (name: string) => {
    const newBrand = await createBrand(name, userId || undefined);
    // Reload brands and select the new one
    await loadBrands();
    setSelectedBrandId(newBrand.id);
    await loadFamiliesForBrand(newBrand.id);
  };

  const handleCreateFamily = async (name: string) => {
    if (!selectedBrandId) return;
    // TODO: Add category selection in future update
    // For now, create family without category (can be added later)
    const newFamily = await createProductFamily(selectedBrandId, name, userId || undefined, null);
    // Reload families and select the new one
    await loadFamiliesForBrand(selectedBrandId);
    setSelectedFamilyId(newFamily.id);
    await loadModelsForFamily(newFamily.id);
  };

  const handleCreateModel = async (name: string) => {
    if (!selectedBrandId || !selectedFamilyId) {
      // brand and family are denormalised onto the model; we can't create
      // without both. Surface the requirement instead of silently dropping
      // the user's input.
      Alert.alert('Select a brand and family first', 'Choose a brand and family before adding a new model.');
      return;
    }
    const newModel = await createProductModel(selectedFamilyId, name, selectedBrandId, userId || undefined);
    // Reload models and select the new one
    await loadModelsForFamily(selectedFamilyId);
    setSelectedModelId(newModel.id);
  };

  // Determine if brand/model should be editable based on confidence
  const shouldAllowBrandModelEdit = () => {
    // Allow editing if:
    // 1. Confidence is null/undefined (no confidence data)
    // 2. Confidence is below HIGH threshold (0.9)
    // 3. Brand is "Unknown"
    const HIGH_CONFIDENCE_THRESHOLD = 0.9;
    const isUnknownBrand = brand?.toLowerCase() === 'unknown' || !brand;
    const isLowConfidence = confidenceScore === null || confidenceScore < HIGH_CONFIDENCE_THRESHOLD;

    return isUnknownBrand || isLowConfidence;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      console.log('💾 Saving item...');
      console.log('  - isEditingBrandModel:', isEditingBrandModel);
      console.log('  - selectedBrandId:', selectedBrandId);
      console.log('  - selectedFamilyId:', selectedFamilyId);
      console.log('  - selectedModelId:', selectedModelId);

      let variantId = item?.variant_id;

      // If brand/family/model were edited and changed, create new hierarchy
      if (isEditingBrandModel && selectedBrandId && selectedFamilyId && selectedModelId) {
        const selectedBrand = brands.find(b => b.id === selectedBrandId);
        const selectedFamily = families.find(f => f.id === selectedFamilyId);
        const selectedModel = models.find(m => m.id === selectedModelId);

        if (selectedBrand && selectedFamily && selectedModel) {
          // Check if this is different from current brand/family/model
          const currentBrandId = item?.variant?.model?.family?.brand?.id?.toString();
          const currentFamilyId = item?.variant?.model?.family?.id?.toString();
          const currentModelId = item?.variant?.model?.id?.toString();

          if (selectedBrandId !== currentBrandId ||
              selectedFamilyId !== currentFamilyId ||
              selectedModelId !== currentModelId) {
            console.log('🔄 Creating new product hierarchy...');
            console.log('  - Brand:', selectedBrand.name);
            console.log('  - Family:', selectedFamily.name);
            console.log('  - Model:', selectedModel.name);

            // Create new product hierarchy
            const hierarchy = await findOrCreateProductHierarchy(
              selectedBrand.name,
              selectedModel.name,
              selectedFamily.name, // Use selected family name
              item?.variant?.attributes || {}
            );
            variantId = hierarchy.variantId;
            console.log('✅ New variant ID:', variantId);
          }
        }
      } else if (isEditingBrandModel) {
        // User started editing but didn't complete the selection
        console.warn('⚠️ Incomplete brand/family/model selection');
        Alert.alert(
          'Incomplete Selection',
          'Please select a Brand, Family, and Model before saving.',
          [{ text: 'OK' }]
        );
        setSaving(false);
        return;
      }

      const updates: any = {
        condition,
        // V3 P7: no valuation write — the item's value belongs to the P8 projector (C9).
        purchase_price: purchasePrice ? parseFloat(purchasePrice) : null,
        purchase_period: purchaseDate,
        // Update image_analysis with new description and category
        image_analysis: {
          ...((item?.image_analysis as any) || {}),
          description,
          category,
        },
      };

      // A deliberate variant pick (T4b) takes precedence over the
      // hierarchy-derived variantId — but ONLY when the user is not also
      // editing the brand/family/model hierarchy. The sibling list is loaded
      // from the original model and is not reloaded for a newly selected
      // model, so honouring a stale pick while the hierarchy is being edited
      // would save a variant from the old model over the new selection. When
      // editing the hierarchy, the derived variantId wins. variant_source='user'
      // marks the pick as a user assertion (C5). V3 P7: no valuation/currency
      // co-write — the sibling's catalogue price is not this item's value.
      if (pickedVariant && !isEditingBrandModel) {
        variantId = pickedVariant.variantId;
      }

      // Only update variant_id if it changed
      if (variantId && variantId !== item?.variant_id) {
        console.log('📝 Updating variant_id:');
        console.log('  - Old:', item?.variant_id);
        console.log('  - New:', variantId);
        updates.variant_id = variantId;
        if (pickedVariant && pickedVariant.variantId === variantId) {
          updates.variant_source = 'user';
        }
      }

      console.log('💾 Calling updateUserProduct with updates:', updates);
      const updatedProduct = await updateUserProduct(itemId, updates);
      console.log('📦 Updated product:', updatedProduct ? 'Success' : 'Failed');

      if (!updatedProduct) {
        throw new Error('Failed to update product');
      }

      navigation.navigate('ItemDetail', { itemId });
    } catch (error) {
      console.error('Error saving item:', error);
      Alert.alert('Error', 'Failed to save item. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    console.log('Delete button pressed, itemId:', itemId);
    console.log('Current state:', { deleting, showDeleteConfirm });
    setShowDeleteConfirm(true);
    console.log('Set showDeleteConfirm to true');
  };

  const confirmDelete = async () => {
    try {
      console.log('Delete confirmed, starting deletion process...');
      setDeleting(true);

      // Delete images from storage if they exist
      if (item?.images) {
        const images = item.images as any;
        if (images && Array.isArray(images) && images.length > 0) {
          const imagePaths = images.map((img: any) => img.storage_path || img).filter(Boolean);
          if (imagePaths.length > 0) {
            console.log('Attempting to delete images:', imagePaths);
            const { error: storageError } = await supabase.storage
              .from('item-images')
              .remove(imagePaths);

            if (storageError) {
              console.error('Error deleting images:', storageError);
            } else {
              console.log('Images deleted successfully');
            }
          }
        }
      }

      // Delete item from database using the new utility function
      console.log('Attempting to delete user product from database:', itemId);
      await deleteUserProduct(itemId);

      console.log('Delete successful, popping to CacheList');
      navigation.popToTop();
    } catch (error) {
      console.error('Error in delete process:', error);
      Alert.alert('Error', 'Failed to delete item. Please try again.');
    } finally {
      console.log('Delete process complete, cleaning up...');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <View style={[commonStyles.safeArea, styles.centered]}>
        <ActivityIndicator size={36} color={theme.colors.accent} />
      </View>
    );
  }

  return (
    <SafeAreaView style={commonStyles.safeArea}>
      <ScrollView style={styles.scrollView}>
        {imageUrl && (
          <View style={styles.imageContainer}>
            <Image source={{ uri: imageUrl }} style={styles.image} />
          </View>
        )}

        <View style={styles.formContainer}>
          <View style={styles.fieldGroup}>
            <Text style={commonStyles.inputLabel}>Valuation</Text>
            <View>
              {/* V3 P6/P7: the item's own valuation state, read-only */}
              {item && <ItemValuation item={item} />}
            </View>
          </View>

          {/* Confidence Score Display */}
          {confidenceScore !== null && (
            <View style={styles.fieldGroup}>
              <Text style={commonStyles.inputLabel}>AI Confidence</Text>
              <View style={[
                styles.confidenceBadge,
                { borderColor: confidenceTone(confidenceScore) },
              ]}>
                <Text style={[
                  styles.confidenceText,
                  { color: confidenceTone(confidenceScore) },
                ]}>
                  {(confidenceScore * 100).toFixed(0)}%
                </Text>
              </View>
            </View>
          )}

          {/* Brand / family / model. Read-only as detail rows (GET-266); the Edit
              button switches to the hierarchy pickers below. */}
          {!isEditingBrandModel ? (
            <View style={styles.fieldGroup}>
              <DetailGroup>
                <DetailRow label="Brand" value={item?.variant?.model?.family?.brand?.name || 'N/A'} />
                <DetailRow label="Family" value={item?.variant?.model?.family?.name || 'N/A'} />
                <DetailRow label="Model" value={item?.variant?.model?.name || 'N/A'} />
              </DetailGroup>
              {shouldAllowBrandModelEdit() && (
                <TouchableOpacity
                  testID="edit-brand-model"
                  style={[styles.editButton, styles.identityEditButton]}
                  onPress={() => {
                    // Switching to a hierarchy edit invalidates any variant
                    // pick made against the original model — clear it so a
                    // stale pick can't override the new selection on save.
                    setIsEditingBrandModel(true);
                    setPickedVariant(null);
                    setShowVariantPicker(false);
                  }}
                >
                  <Ionicons name="create-outline" size={20} color={theme.colors.accent} />
                  <Text style={styles.editButtonText}>Edit</Text>
                </TouchableOpacity>
              )}
            </View>
          ) : (
            <>
              <View style={styles.fieldGroup}>
                <Text style={commonStyles.inputLabel}>
                  Brand <Text style={styles.editableHint}>(editable)</Text>
                </Text>
                <TouchableOpacity
                  style={styles.pickerButton}
                  onPress={() => {
                    setIsEditingBrandModel(true);
                    setShowBrandPicker(true);
                  }}
                >
                  <Text style={selectedBrandId ? styles.selectedText : styles.placeholderText}>
                    {selectedBrandId
                      ? brands.find(b => b.id === selectedBrandId)?.name
                      : 'Select a brand'}
                  </Text>
                  <Ionicons name="chevron-down" size={20} color={theme.colors.textSecondary} />
                </TouchableOpacity>
              </View>

              <View style={styles.fieldGroup}>
                <Text style={commonStyles.inputLabel}>
                  Family <Text style={styles.editableHint}>(editable)</Text>
                </Text>
                <TouchableOpacity
                  style={[styles.pickerButton, !selectedBrandId && commonStyles.primaryButtonDisabled]}
                  onPress={() => selectedBrandId && setShowFamilyPicker(true)}
                  disabled={!selectedBrandId}
                  accessibilityState={{ disabled: !selectedBrandId }}
                  testID="family-picker-button"
                >
                  <Text
                    style={[
                      selectedFamilyId ? styles.selectedText : styles.placeholderText,
                      !selectedBrandId && commonStyles.disabledButtonText,
                    ]}
                  >
                    {selectedFamilyId
                      ? families.find(f => f.id === selectedFamilyId)?.name
                      : selectedBrandId
                        ? 'Select a family'
                        : 'Select a brand first'}
                  </Text>
                  <Ionicons name="chevron-down" size={20} color={theme.colors.textSecondary} />
                </TouchableOpacity>
              </View>

              <View style={styles.fieldGroup}>
                <Text style={commonStyles.inputLabel}>
                  Model <Text style={styles.editableHint}>(editable)</Text>
                </Text>
                <TouchableOpacity
                  style={[styles.pickerButton, !selectedFamilyId && commonStyles.primaryButtonDisabled]}
                  onPress={() => selectedFamilyId && setShowModelPicker(true)}
                  disabled={!selectedFamilyId}
                  accessibilityState={{ disabled: !selectedFamilyId }}
                  testID="model-picker-button"
                >
                  <Text
                    style={[
                      selectedModelId ? styles.selectedText : styles.placeholderText,
                      !selectedFamilyId && commonStyles.disabledButtonText,
                    ]}
                  >
                    {selectedModelId
                      ? models.find(m => m.id === selectedModelId)?.name
                      : selectedFamilyId
                        ? 'Select a model'
                        : 'Select a family first'}
                  </Text>
                  <Ionicons name="chevron-down" size={20} color={theme.colors.textSecondary} />
                </TouchableOpacity>
              </View>
            </>
          )}

          {isEditingBrandModel && (
            <TouchableOpacity
              style={styles.cancelEditButton}
              onPress={() => {
                setIsEditingBrandModel(false);
                // Reset to original values
                const brandId = item?.variant?.model?.family?.brand?.id;
                const familyId = item?.variant?.model?.family?.id;
                const modelId = item?.variant?.model?.id;
                setSelectedBrandId(brandId?.toString() || '');
                setSelectedFamilyId(familyId?.toString() || '');
                setSelectedModelId(modelId?.toString() || '');
              }}
            >
              <Text style={styles.cancelEditText}>Cancel Edit</Text>
            </TouchableOpacity>
          )}

          {/* Choose the right variant (T4b). Shown only when the catalog has
              ≥2 sibling variants for this model (Q2) and the user is not
              editing the brand/family/model hierarchy — the sibling list is
              bound to the original model, so it is meaningless (and would save
              a stale variant) while the model itself is being changed. On
              select the pick is stashed and applied by handleSave (variant_id
              + variant_source='user'; no valuation, V3 P7). */}
          {!isEditingBrandModel && siblingVariants.length >= 2 && (
            <View style={styles.fieldGroup}>
              {!showVariantPicker ? (
                <TouchableOpacity
                  testID="open-variant-picker"
                  accessibilityRole="button"
                  onPress={() => setShowVariantPicker(true)}
                >
                  <Text style={commonStyles.inputLabel}>
                    {pickedVariant ? 'Variant selected ✓ (tap to change)' : 'Choose the right variant'}
                  </Text>
                </TouchableOpacity>
              ) : (
                <VariantPicker
                  showPrices={!item?.restoration_valuation}
                  variants={siblingVariants}
                  onSelect={(variant) => {
                    setPickedVariant(variant);
                    setShowVariantPicker(false);
                  }}
                />
              )}
            </View>
          )}

          <View style={styles.fieldGroup}>
            <Text style={commonStyles.inputLabel}>Name</Text>
            <TextInput
              style={commonStyles.input}
              value={name}
              onChangeText={setName}
              placeholder="Item name"
              placeholderTextColor={theme.colors.textTertiary}
            />
          </View>

          <View style={styles.fieldGroup}>
            <Text style={commonStyles.inputLabel}>Description</Text>
            <TextInput
              style={[commonStyles.input, styles.textArea]}
              value={description}
              onChangeText={setDescription}
              placeholder="Item description"
              placeholderTextColor={theme.colors.textTertiary}
              multiline
              numberOfLines={4}
            />
          </View>

          <View style={styles.fieldGroup}>
            <DetailGroup>
              <DetailRow label="Category" value={category || 'N/A'} />
              <DetailRow label="Condition" value={<ConditionBadge condition={condition} />} />
              <DetailRow label="Currency" value={item?.currency || 'USD'} />
            </DetailGroup>
          </View>

          <View style={styles.fieldGroup}>
            <Text style={commonStyles.inputLabel}>Original Value ($)</Text>
            <TextInput
              style={commonStyles.input}
              value={purchasePrice}
              onChangeText={setPurchasePrice}
              placeholder="0.00"
              placeholderTextColor={theme.colors.textTertiary}
              keyboardType="decimal-pad"
            />
          </View>
        </View>
      </ScrollView>

      <View style={styles.footer}>
        <TouchableOpacity
          style={[commonStyles.destructiveButton, styles.footerButton, deleting && commonStyles.disabledButton]}
          onPress={handleDelete}
          disabled={deleting}
        >
          {deleting ? (
            <ActivityIndicator color={theme.colors.textPrimary} />
          ) : (
            <Text style={commonStyles.destructiveButtonText}>Delete Item</Text>
          )}
        </TouchableOpacity>

        <TouchableOpacity
          style={[commonStyles.primaryButton, styles.footerButton, saving && commonStyles.disabledButton]}
          onPress={handleSave}
          disabled={saving}
        >
          <Text style={commonStyles.primaryButtonText}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Delete Confirmation Modal */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={showDeleteConfirm}
        onRequestClose={() => setShowDeleteConfirm(false)}
      >
        <View style={commonStyles.modalOverlay}>
          <View style={commonStyles.modalContent}>
            <Text style={commonStyles.modalTitle}>Delete Item</Text>
            <Text style={commonStyles.modalText}>
              Are you sure you want to delete this item? This action cannot be undone.
            </Text>
            <View style={styles.dialogButtonRow}>
              <TouchableOpacity
                style={[commonStyles.secondaryButton, styles.dialogButton]}
                onPress={() => setShowDeleteConfirm(false)}
              >
                <Text style={commonStyles.secondaryButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[commonStyles.destructiveButton, styles.dialogButton]}
                onPress={confirmDelete}
              >
                <Text style={commonStyles.destructiveButtonText}>Delete</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Brand Picker Modal */}
      <Modal
        animationType="slide"
        transparent={true}
        visible={showBrandPicker}
        onRequestClose={() => setShowBrandPicker(false)}
      >
        <View style={styles.bottomSheetOverlay}>
          <View style={styles.bottomSheet}>
            <View style={styles.pickerHeader}>
              <Text style={styles.pickerTitle}>Select Brand</Text>
              <TouchableOpacity onPress={() => setShowBrandPicker(false)}>
                <Ionicons name="close" size={24} color={theme.colors.textPrimary} />
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.pickerList}>
              {/* Add New Brand Button */}
              {isVerified ? (
                <TouchableOpacity
                  style={styles.addNewButton}
                  onPress={() => {
                    setShowBrandPicker(false);
                    setShowCreateBrand(true);
                  }}
                >
                  <Ionicons name="add-circle" size={20} color={theme.colors.accent} />
                  <Text style={styles.addNewButtonText}>Add New Brand</Text>
                </TouchableOpacity>
              ) : (
                <View style={styles.verificationMessage}>
                  <Ionicons name="mail-outline" size={20} color={theme.colors.textSecondary} />
                  <Text style={styles.verificationMessageText}>
                    Verify your email to add new brands
                  </Text>
                </View>
              )}

              {brands.map((brand) => (
                <TouchableOpacity
                  key={brand.id}
                  style={[
                    styles.pickerItem,
                    selectedBrandId === brand.id && styles.pickerItemSelected,
                  ]}
                  onPress={() => {
                    handleBrandChange(brand.id);
                    setShowBrandPicker(false);
                  }}
                >
                  <Text style={[
                    styles.pickerItemText,
                    selectedBrandId === brand.id && styles.pickerItemTextSelected,
                  ]}>
                    {brand.name}
                  </Text>
                  {selectedBrandId === brand.id && (
                    <Ionicons name="checkmark" size={20} color={theme.colors.accent} />
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Family Picker Modal */}
      <Modal
        animationType="slide"
        transparent={true}
        visible={showFamilyPicker}
        onRequestClose={() => setShowFamilyPicker(false)}
      >
        <View style={styles.bottomSheetOverlay}>
          <View style={styles.bottomSheet}>
            <View style={styles.pickerHeader}>
              <Text style={styles.pickerTitle}>Select Family</Text>
              <TouchableOpacity onPress={() => setShowFamilyPicker(false)}>
                <Ionicons name="close" size={24} color={theme.colors.textPrimary} />
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.pickerList}>
              {/* Add New Family Button */}
              {isVerified ? (
                <TouchableOpacity
                  style={styles.addNewButton}
                  onPress={() => {
                    setShowFamilyPicker(false);
                    setShowCreateFamily(true);
                  }}
                >
                  <Ionicons name="add-circle" size={20} color={theme.colors.accent} />
                  <Text style={styles.addNewButtonText}>Add New Family</Text>
                </TouchableOpacity>
              ) : (
                <View style={styles.verificationMessage}>
                  <Ionicons name="mail-outline" size={20} color={theme.colors.textSecondary} />
                  <Text style={styles.verificationMessageText}>
                    Verify your email to add new families
                  </Text>
                </View>
              )}

              {families.length === 0 ? (
                <View style={styles.emptyState}>
                  <Text style={styles.emptyStateText}>No families found for this brand</Text>
                </View>
              ) : (
                families.map((family) => (
                  <TouchableOpacity
                    key={family.id}
                    style={[
                      styles.pickerItem,
                      selectedFamilyId === family.id && styles.pickerItemSelected,
                    ]}
                    onPress={() => {
                      handleFamilyChange(family.id);
                      setShowFamilyPicker(false);
                    }}
                  >
                    <Text style={[
                      styles.pickerItemText,
                      selectedFamilyId === family.id && styles.pickerItemTextSelected,
                    ]}>
                      {family.name}
                    </Text>
                    {selectedFamilyId === family.id && (
                      <Ionicons name="checkmark" size={20} color={theme.colors.accent} />
                    )}
                  </TouchableOpacity>
                ))
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Model Picker Modal */}
      <Modal
        animationType="slide"
        transparent={true}
        visible={showModelPicker}
        onRequestClose={() => setShowModelPicker(false)}
      >
        <View style={styles.bottomSheetOverlay}>
          <View style={styles.bottomSheet}>
            <View style={styles.pickerHeader}>
              <Text style={styles.pickerTitle}>Select Model</Text>
              <TouchableOpacity onPress={() => setShowModelPicker(false)}>
                <Ionicons name="close" size={24} color={theme.colors.textPrimary} />
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.pickerList}>
              {/* Add New Model Button */}
              {isVerified ? (
                <TouchableOpacity
                  style={styles.addNewButton}
                  onPress={() => {
                    setShowModelPicker(false);
                    setShowCreateModel(true);
                  }}
                >
                  <Ionicons name="add-circle" size={20} color={theme.colors.accent} />
                  <Text style={styles.addNewButtonText}>Add New Model</Text>
                </TouchableOpacity>
              ) : (
                <View style={styles.verificationMessage}>
                  <Ionicons name="mail-outline" size={20} color={theme.colors.textSecondary} />
                  <Text style={styles.verificationMessageText}>
                    Verify your email to add new models
                  </Text>
                </View>
              )}

              {models.length === 0 ? (
                <View style={styles.emptyState}>
                  <Text style={styles.emptyStateText}>No models found for this family</Text>
                </View>
              ) : (
                models.map((model) => (
                  <TouchableOpacity
                    key={model.id}
                    style={[
                      styles.pickerItem,
                      selectedModelId === model.id && styles.pickerItemSelected,
                    ]}
                    onPress={() => {
                      handleModelChange(model.id);
                      setShowModelPicker(false);
                    }}
                  >
                    <Text style={[
                      styles.pickerItemText,
                      selectedModelId === model.id && styles.pickerItemTextSelected,
                    ]}>
                      {model.name}
                    </Text>
                    {selectedModelId === model.id && (
                      <Ionicons name="checkmark" size={20} color={theme.colors.accent} />
                    )}
                  </TouchableOpacity>
                ))
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Creation Modals */}
      <CreateItemModal
        visible={showCreateBrand}
        onClose={() => setShowCreateBrand(false)}
        onSubmit={handleCreateBrand}
        title="Add New Brand"
        placeholder="e.g., Apple, Samsung, Sony"
        label="Brand Name"
        existingBrands={brands}
        enableFuzzyMatching={true}
        onSelectExisting={(brand) => {
          setSelectedBrandId(brand.id);
          loadFamiliesForBrand(brand.id);
        }}
      />

      <CreateItemModal
        visible={showCreateFamily}
        onClose={() => setShowCreateFamily(false)}
        onSubmit={handleCreateFamily}
        title="Add New Family"
        placeholder="e.g., iPhone, Galaxy, PlayStation"
        label="Family Name"
      />

      <CreateItemModal
        visible={showCreateModel}
        onClose={() => setShowCreateModel(false)}
        onSubmit={handleCreateModel}
        title="Add New Model"
        placeholder="e.g., 13 Pro, S21 Ultra, 5"
        label="Model Name"
      />
    </SafeAreaView>
  );
}

